import React from 'react';
import { useAtom } from 'jotai';
import { Checkbox } from '@carbon/react';
import mapStyle from 'components/map/map.module.scss';
import { CollapsibleSection } from 'components/map/collapsible-section';
import scopedWellStyle from './well-scoped-layers.module.scss';
import classnames from 'classnames';
import {
  sidetrackVisibilityAtom,
  isProducingVisibleAtom,
  isUnitsVisibleAtom,
} from './atoms';

export interface WellboreSummary {
  producing: number;
  sidetracks: string[]; // UWI14 identifiers for sidetracks
}

export const WellScopedLayers: React.FC<{
  hasIntersectingUnits: boolean;
  wellboreSummary?: WellboreSummary;
}> = ({ hasIntersectingUnits, wellboreSummary }) => {
  const [isProducingVisible, setIsProducingVisible] = useAtom(
    isProducingVisibleAtom
  );
  const [sidetrackVisibility, setSidetrackVisibility] = useAtom(
    sidetrackVisibilityAtom
  );
  const [isUnitsVisible, setIsUnitsVisible] = useAtom(isUnitsVisibleAtom);

  const hasSidetracks =
    wellboreSummary && wellboreSummary.sidetracks.length > 0;

  const toggleSidetrack = (uwi14: string) => {
    setSidetrackVisibility((prev) => ({
      ...prev,
      [uwi14]: !prev[uwi14],
    }));
  };

  // Calculate if all wells options are checked (for indeterminate state)
  const allSidetracksChecked = hasSidetracks
    ? wellboreSummary.sidetracks.every((uwi14) => sidetrackVisibility[uwi14])
    : true;
  const someSidetracksChecked = hasSidetracks
    ? wellboreSummary.sidetracks.some((uwi14) => sidetrackVisibility[uwi14])
    : false;

  const allWellsChecked = isProducingVisible && allSidetracksChecked;
  const someWellsChecked = isProducingVisible || someSidetracksChecked;
  const isIndeterminate = someWellsChecked && !allWellsChecked;

  const handleWellsToggle = (
    _evt: React.ChangeEvent<HTMLInputElement>,
    { checked }: { checked: boolean }
  ) => {
    setIsProducingVisible(checked);
    if (hasSidetracks) {
      const newVisibility: Record<string, boolean> = {};
      wellboreSummary.sidetracks.forEach((uwi14) => {
        newVisibility[uwi14] = checked;
      });
      setSidetrackVisibility(newVisibility);
    }
  };

  return (
    <div className={scopedWellStyle.scopedLayersContainer}>
      <CollapsibleSection
        id="scoped-wells"
        title="Wells"
        checked={allWellsChecked}
        indeterminate={isIndeterminate}
        onChange={handleWellsToggle}
        defaultOpen={true}
      >
        <div
          className={`${mapStyle.wellsCheck} ${scopedWellStyle.scopedProducing}`}
        >
          <Checkbox
            id="producing-visibility"
            labelText="Subject"
            checked={isProducingVisible}
            onChange={() => setIsProducingVisible(!isProducingVisible)}
          />
        </div>

        {hasSidetracks &&
          wellboreSummary.sidetracks.map((uwi14, index) => (
            <div
              key={uwi14}
              className={classnames(
                mapStyle.wellsCheck,
                scopedWellStyle.scopedSidetrack
              )}
            >
              <Checkbox
                id={`sidetrack-visibility-${index}`}
                labelText={`Sidetrack (${uwi14.slice(-4)})`}
                checked={sidetrackVisibility[uwi14] ?? false}
                onChange={() => toggleSidetrack(uwi14)}
              />
            </div>
          ))}
      </CollapsibleSection>

      <CollapsibleSection
        id="scoped-development-areas"
        title="Development Areas"
        checked={hasIntersectingUnits ? isUnitsVisible : false}
        onChange={(_evt, { checked }) => setIsUnitsVisible(checked)}
        defaultOpen={true}
        disabled={!hasIntersectingUnits}
      >
        <div
          className={`${mapStyle.wellsCheck} ${scopedWellStyle.scopedUnits}`}
        >
          <Checkbox
            id="units-visibility"
            labelText="Intersecting Units"
            disabled={!hasIntersectingUnits}
            checked={hasIntersectingUnits ? isUnitsVisible : false}
            onChange={() => setIsUnitsVisible(!isUnitsVisible)}
          />
        </div>
      </CollapsibleSection>
    </div>
  );
};
